'use client';

import { useCallback } from 'react';

import { ZoneBookingCard as SharedZoneBookingCard } from '@m/components-ui/reservation/zone-booking-card';
import { localISO8601String } from '@m/utils/dateTimeUtils';
import { withAttributionParams } from '@m/utils/reservationFunnelContext';

import { FIND_PARKING_URL } from '@/constants/links';
import { checkZoneAvailability } from '@/services/zoneAvailabilityService';

interface ZoneBookingCardProps {
  bookNowUrl: string;
  coordinates?: { latitude: number; longitude: number };
  siteId: string;
  /** IANA timezone of the lot. Kept for call-site compatibility; unused — wall-clock is sent as-is. */
  timezone?: string;
}

export function ZoneBookingCard({ bookNowUrl, coordinates, siteId }: ZoneBookingCardProps) {
  const findParkingUrl = coordinates
    ? `${FIND_PARKING_URL}?lat=${coordinates.latitude}&lng=${coordinates.longitude}`
    : FIND_PARKING_URL;

  const handleCheckAvailability = useCallback(
    (start: Date, end: Date, signal: AbortSignal) => {
      if (!coordinates) return Promise.resolve({ available: false as const });
      return checkZoneAvailability(
        {
          zoneId: siteId,
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          // Wall-clock with a literal Z (market-api requires an offset) — same contract as iOS.
          arrivalDateTime: localISO8601String(start),
          returnDateTime: localISO8601String(end),
        },
        signal,
      );
    },
    [siteId, coordinates],
  );

  const handleBookNow = useCallback(
    (start: Date, end: Date) => {
      // Pass picker wall-clock via getTime(); checkout formats with localISO8601String.
      const params = new URLSearchParams({
        zone: siteId,
        start: String(start.getTime()),
        end: String(end.getTime()),
      });
      window.open(
        withAttributionParams(`${bookNowUrl}/reserve-parking?${params.toString()}`),
        '_blank',
        'noopener,noreferrer',
      );
    },
    [bookNowUrl, siteId],
  );

  return (
    <SharedZoneBookingCard
      findParkingUrl={findParkingUrl}
      onCheckAvailability={handleCheckAvailability}
      onBookNow={handleBookNow}
    />
  );
}
